import { AppBase } from '@jollywise/jollygoodgame';
import { KEYS, SCENE_MAP } from 'game/constants/SceneConstants';
import * as assetPackData from '../assets/assetpack.json';
import HudConfig from 'game/constants/HudConfig';
import { GameShortcuts } from './game/utils/GameShortcuts';

import { FONTS } from './game/constants/AppFonts';
import { PlayerModel } from 'game/model/PlayerModel';
import { ScoreModel } from 'game/model/ScoreModel';
import { ScenarioModel } from 'game/model/ScenarioModel';

import { trackCountry } from './game/utils/GaTracking';

export class App extends AppBase {
  constructor(opts) {
    super(opts);

    // removed (.*dev-).*fb-app-hosting.com.*/.test(window.location.href) to allow api call on staging
    this._devMode = __WATCH__ || /.*fb-app-hosting.com.*/.test(window.location.href);
    this._displayCheats = false;
    this._apiUrl = 'https://staging-chn.fb-app-hosting.com/api/data/negotiation';
    this._geoIpUrl = `${
      this._devMode ? 'https://staging-chn.fb-app-hosting.com' : window.location.origin
    }/api/geoip`;
  }

  // bootGame is called when app is initialized and ready to start doing stuff
  async bootGame() {
    if (window.location.origin) {
      if (__WATCH__ || window.location.origin.includes('dev-')) {
        this._displayCheats = true;
        this.appUrls.setBasePath('./');
      } else if (window.location.origin.includes('staging')) {
        this.appUrls.setBasePath('https://1244060897.rsc.cdn77.org/game/dist/');
      } else {
        this.appUrls.setBasePath('https://1877923700.rsc.cdn77.org/game/dist/');
      }
    }

    if (this._devMode) {
      const result = await this._loadCMSData(this._apiUrl)
        .then((data) => {
          if (data.success) {
            this.cache.json.remove('gamedata');
            this.cache.json.add('gamedata', data.result);
            console.log(`%c Replacing data from cms`, 'color:#00ff00', data.result);
          }
        })
        .catch((e) => {
          console.warn('nope', e);
        });
    }

    // set base urls
    // grab the base assetpack
    const assetPack = {
      ...assetPackData,
    };

    // trigger game boot
    this.loader.bootGame(
      {
        // boot should only load in assets required for the loadscreen to function
        boot: {
          fonts: FONTS.BOOT,
          assetpacks: [{ config: assetPack, keys: ['boot', 'boot_spritesheets'] }],
        },
        // remaining assets should be included in load so that progress is shown
        load: {
          assetpacks: [
            { config: assetPack, keys: Object.keys(assetPack).filter((k) => k != 'boot') },
          ],
        },
      },
      () => {
        this.assetsLoaded();
      }
    );
  }

  assetsLoaded() {
    const gamedata = this.cache.json.get('gamedata') || {};

    // track country
    this.triggerGeoIpTrack();

    // register hud
    this._setupHud();

    // register models
    this._scores = new ScoreModel();
    this._player = new PlayerModel();
    this._scenario = new ScenarioModel({ game: this, data: gamedata });
    this._shortcuts = new GameShortcuts(this, this.shortcuts, this._displayCheats);

    // register copy so that the copy parser can pick it up
    const copyxml = this.cache.xml.get('copy_xml');
    if (copyxml) this.copy.addCopyFromXML(copyxml);
    // add the scene map to scene controller so that it knows what you want
    this.sceneController.addSceneMap(SCENE_MAP);
    // enter application
    this.showWelcome();
  }

  // =============================================================================================
  // HUD eventS
  // =============================================================================================

  _setupHud() {
    this.hud.setHudConfig(HudConfig);
    this.hud.events.on('PAUSE_GAME', this.pauseGame, this);
    this.hud.events.on('RESUME_GAME', this.resumeGame, this);
    this.hud.events.on('RETURN_HOME', this.returnHome, this);
    this.hud.events.on('START_GAME', this.introScene, this);
    this.hud.events.on('REPLAY_GAME', this.introScene, this);
    this.hud.events.on('SHOW_INSTRUCTIONS', this.showHowToPlay, this);
    this.hud.events.on('CLOSE_INSTRUCTIONS', this.closeHowToPlay, this);
    // this.hud.events.on('SHOW_SETTINGS', this.settings.showSettings, this.settings);
    this.hud.events.on('TOGGLE_SOUND', this._toggleAudio, this);
    this.hud.events.on('PLAYERS_SCORES', this.scores, this);
  }

  // =============================================================================================
  // sound
  // =============================================================================================

  _toggleAudio() {
    this.settings.toggleAudio();
  }

  playMusic(id, opts) {
    if (this.musicId != id) {
      this.musicId = id;
      this.soundController.playMusic(id, opts);
    }
  }
  stopMusic(opts) {
    this.musicId = '';
    this.soundController.stopMusic(opts);
  }

  // =============================================================================================
  // HOW TO PLAY
  // =============================================================================================

  showHowToPlay() {
    this.sceneController.showOverlayScene(KEYS.HowToPlay, {
      data: { content: HudConfig.howtoplay },
    });
    if (this.paused) this.sceneController.resumeScene(KEYS.HowToPlay);
    this.hud.setState('HOW_TO_PLAY');
  }

  closeHowToPlay() {
    this.sceneController.removeOverlayScene();
    this.hud.resumePreviousState();
  }

  // =============================================================================================
  // SCENES
  // =============================================================================================

  returnHome() {
    if (this.paused) this.resumeGame();
    this.showWelcome();
  }

  introScene() {
    this._player.resetPlayersData();
    this._scores.resetScores();
    this._scenario.resetData();
    this._player.trackingPlaySession = {};
    this.sceneController.switchScene(KEYS.Intro);
  }

  outroScene() {
    this.sceneController.switchScene(KEYS.Outro);
    this.soundController.playMusic('chn_intro_music');
  }

  startGame() {
    this.sceneController.switchScene(KEYS.Scenario);
    this.soundController.stopMusic();
  }

  startDecision() {
    this.sceneController.switchScene(KEYS.ScenarioDecision);
  }

  startOutcome() {
    this.sceneController.switchScene(KEYS.ScenarioOutcome);
  }

  showWelcome() {
    // switch to welsome screen
    this.sceneController.switchScene(KEYS.Welcome, { data: {} });

    this.soundController.playMusic('chn_intro_music');
  }

  // =============================================================================================
  // overlays
  // =============================================================================================

  scores() {
    this.sceneController.showOverlayScene(KEYS.ModalScores);
  }

  pauseGame() {
    super.pauseGame();
    this.hud.setState('PAUSE');
  }

  resumeGame() {
    super.resumeGame();
    this.hud.resumePreviousState();
  }

  // =============================================================================================
  // CMS
  // =============================================================================================

  _loadCMSData(url) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('GET', url, true);
      request.onload = (e) => {
        const request = e.currentTarget;
        if (request.status >= 200 && request.status < 400) {
          const data = JSON.parse(request.response);
          if (!this.devMode) {
            resolve(data);
          } else if (data.success) {
            resolve(data.result);
          } else {
            reject();
          }
        } else {
          reject(e);
        }
      };
      request.send();
    });
  }

  // =============================================================================================
  // GEO IP
  // =============================================================================================

  async triggerGeoIpTrack() {
    const data = await this.fetchGeoIp(this._geoIpUrl);
    if (data.success) {
      trackCountry({ country: data.country });
    } else {
      console.log('issue getting geoip data: ', data?.reason || 'reason unknown');
    }
  }

  fetchGeoIp(url) {
    return window
      .fetch(url)
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        return { success: false, reason: error.message };
      });
  }
}
